import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import type { PluginListenerHandle } from '@capacitor/core';
import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../lib/api.js';
import type { SessionResponse } from '../types.js';
import { AppShell } from './AppShell.js';
import { LoginPage } from '../pages/LoginPage.js';
import { OnboardingPage } from '../pages/OnboardingPage.js';
import { TodayPage } from '../pages/TodayPage.js';

const TrainPage = lazy(() => import('../pages/TrainPage.js').then((module) => ({ default: module.TrainPage })));
const WorkoutPage = lazy(() => import('../pages/WorkoutPage.js').then((module) => ({ default: module.WorkoutPage })));
const FoodPage = lazy(() => import('../pages/FoodPage.js').then((module) => ({ default: module.FoodPage })));
const MealPlannerPage = lazy(() => import('../pages/MealPlannerPage.js').then((module) => ({ default: module.MealPlannerPage })));
const ProgressPage = lazy(() => import('../pages/ProgressPage.js').then((module) => ({ default: module.ProgressPage })));
const JournalPage = lazy(() => import('../pages/JournalPage.js').then((module) => ({ default: module.JournalPage })));
const IntegrationsPage = lazy(() => import('../pages/IntegrationsPage.js').then((module) => ({ default: module.IntegrationsPage })));
const MorePage = lazy(() => import('../pages/MorePage.js').then((module) => ({ default: module.MorePage })));
const HealthPage = lazy(() => import('../pages/HealthPage.js').then((module) => ({ default: module.HealthPage })));
const ActivityPage = lazy(() => import('../pages/ActivityPage.js').then((module) => ({ default: module.ActivityPage })));
const MobilityPage = lazy(() => import('../pages/MobilityPage.js').then((module) => ({ default: module.MobilityPage })));
const PlanPage = lazy(() => import('../pages/PlanPage.js').then((module) => ({ default: module.PlanPage })));
const ReportsPage = lazy(() => import('../pages/ReportsPage.js').then((module) => ({ default: module.ReportsPage })));
const MotivationPage = lazy(() => import('../pages/MotivationPage.js').then((module) => ({ default: module.MotivationPage })));
const SettingsPage = lazy(() => import('../pages/SettingsPage.js').then((module) => ({ default: module.SettingsPage })));
const ExerciseMediaPage = lazy(() => import('../pages/ExerciseMediaPage.js').then((module) => ({ default: module.ExerciseMediaPage })));
const ExportPage = lazy(() => import('../pages/ExportPage.js').then((module) => ({ default: module.ExportPage })));
const PrivacyPage = lazy(() => import('../pages/PrivacyPage.js').then((module) => ({ default: module.PrivacyPage })));

export function App() {
  const navigate = useNavigate();
  useEffect(() => {
    let handle: PluginListenerHandle | undefined;
    void CapacitorApp.addListener('appUrlOpen', ({ url }) => {
      try {
        const incoming = new URL(url);
        const path = incoming.protocol === 'rahul365:'
          ? `/${incoming.hostname}${incoming.pathname}${incoming.search}`
          : `${incoming.pathname}${incoming.search}`;
        if (incoming.protocol === 'rahul365:' || incoming.hostname === 'gym.quantaum.co.uk') {
          void navigate(path || '/today');
          void Browser.close().catch(() => undefined);
        }
      } catch {
        // Ignore malformed external links; navigation remains inside the app.
      }
    }).then((listener) => { handle = listener; });
    return () => { void handle?.remove(); };
  }, [navigate]);
  const session = useQuery({
    queryKey: ['session'],
    queryFn: () => api<SessionResponse>('/api/v1/auth/session'),
    retry: false,
  });

  if (session.isLoading) {
    return (
      <main className="launch-screen" aria-label="Loading Rahul 365">
        <div className="brand-mark">R365</div>
        <strong>RAHUL 365</strong>
        <span>Preparing today…</span>
      </main>
    );
  }

  const unauthorised = session.error instanceof ApiError && session.error.status === 401;
  if (unauthorised || !session.data) {
    return <LoginPage />;
  }
  if (!session.data.user.onboardingComplete) {
    return <OnboardingPage user={session.data.user} />;
  }

  return (
    <Suspense fallback={<main className="page"><div className="launch-screen launch-screen--page"><span className="brand-mark">R365</span><span>Opening…</span></div></main>}>
      <Routes>
      <Route element={<AppShell user={session.data.user} />}>
        <Route index element={<Navigate replace to="/today" />} />
        <Route path="today" element={<TodayPage />} />
        <Route path="train" element={<TrainPage />} />
        <Route path="train/workout" element={<WorkoutPage />} />
        <Route path="food" element={<FoodPage />} />
        <Route path="food/plan" element={<MealPlannerPage />} />
        <Route path="progress" element={<ProgressPage />} />
        <Route path="journal" element={<JournalPage />} />
        <Route path="integrations" element={<IntegrationsPage />} />
        <Route path="health" element={<HealthPage />} />
        <Route path="health/activity" element={<ActivityPage />} />
        <Route path="health/mobility" element={<MobilityPage />} />
        <Route path="plan" element={<PlanPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="motivation" element={<MotivationPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/exercise-media" element={<ExerciseMediaPage />} />
        <Route path="export" element={<ExportPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="more" element={<MorePage />} />
        <Route path="*" element={<Navigate replace to="/today" />} />
      </Route>
      </Routes>
    </Suspense>
  );
}
